import {
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
  ScrollStrategyOptions
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  TemplateRef
} from '@angular/core';

import { DpTooltipComponent } from '@app/ui-components/tooltip/tooltip.component';

@Directive({
  selector: '[dpTooltip]',
  standalone: true
})
export class DpTooltipDirective implements OnDestroy {
  dpTooltip = input<string>();
  contentTemplate = input<TemplateRef<never>>();

  #mutationObserver?: MutationObserver;
  #overlayRef?: OverlayRef;
  readonly #overlay = inject(Overlay);
  readonly #overlayPositionBuilder = inject(OverlayPositionBuilder);
  readonly #scrollStrategies = inject(ScrollStrategyOptions);
  readonly #elementRef = inject(ElementRef);

  @HostListener('mouseenter')
  show(): void {
    this.#createOverlayRefIfNeeded();
    this.#attachTooltipIfNeeded();
  }

  @HostListener('mouseleave', ['$event'])
  hide(event: MouseEvent): void {
    if (this.#overlayRef && !this.#overlayRef.overlayElement.contains(event.relatedTarget as Node)) {
      this.#closeTooltip();
    }
  }

  ngOnDestroy(): void {
    this.#closeTooltip();
  }

  #createOverlayRefIfNeeded(): void {
    if (!this.#overlayRef) {
      this.#overlayRef = this.#overlay.create({
        positionStrategy: this.#getPositionStrategy(),
        scrollStrategy: this.#scrollStrategies.close()
      });
    }
  }

  #attachTooltipIfNeeded(): void {
    if (this.#overlayRef && !this.#overlayRef.hasAttached()) {
      const tooltipRef: ComponentRef<DpTooltipComponent> = this.#overlayRef.attach(
        new ComponentPortal(DpTooltipComponent)
      );

      tooltipRef.instance.text = this.dpTooltip;
      tooltipRef.instance.contentTemplate = this.contentTemplate;

      const tooltipElement = tooltipRef.location.nativeElement as HTMLElement;
      this.#observeTooltipContent(tooltipElement);
      tooltipElement.addEventListener('mouseleave', this.#onTooltipMouseLeave);
    }
  }

  #getPositionStrategy(): FlexibleConnectedPositionStrategy {
    return this.#overlayPositionBuilder.flexibleConnectedTo(this.#elementRef).withPositions([
      {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        panelClass: 'tooltip__bottom',
        offsetY: -10
      },
      {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
        panelClass: 'tooltip__top',
        offsetY: 10
      }
    ]);
  }

  #observeTooltipContent(target: HTMLElement): void {
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
    }

    this.#mutationObserver = new MutationObserver(() => {
      if (this.#overlayRef?.hasAttached()) {
        this.#overlayRef.updatePositionStrategy(this.#getPositionStrategy());
      }
    });

    this.#mutationObserver.observe(target, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  #closeTooltip(): void {
    this.#overlayRef?.detach();
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = undefined;
  }

  readonly #onTooltipMouseLeave = (event: MouseEvent): void => {
    if (this.#shouldClose(event.relatedTarget)) {
      this.#closeTooltip();
    }
  };

  #shouldClose(target: EventTarget | null): boolean {
    return !(target instanceof Node && (this.#elementRef.nativeElement as HTMLElement).contains(target));
  }
}
